import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useRole } from '../context/RoleContext';
import { usePersona } from '../context/PersonaContext';
import { useOffline } from '../context/OfflineContext';
import { OfflineStatusBadge } from './OfflineIndicator';
import AcumenAICircle from './admin/AcumenAICircle';
import { api, clearAccessToken, API_URL } from '../config/api';
import logger from '../utils/logger';
// Favorites section removed - replaced by user-customizable sidebar in Settings

// Custom hook to fetch sidebar preferences
const useSidebarPreferences = () => {
  const [preferences, setPreferences] = useState({
    visible_items: [],
    item_order: [],
    loaded: false
  });

  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const response = await api.get('/settings/sidebar');
        if (response.success) {
          setPreferences({
            visible_items: response.preferences?.visible_items || [],
            item_order: response.preferences?.item_order || [],
            loaded: true
          });
        }
      } catch (error) {
        logger.error('Failed to load sidebar preferences:', error);
        // On error, show all items (default behavior)
        setPreferences({ visible_items: [], item_order: [], loaded: true });
      }
    };

    fetchPreferences();
    
    // Listen for sidebar preference changes from Settings page
    const handlePreferencesUpdate = () => {
      fetchPreferences();
    };
    window.addEventListener('sidebar-preferences-updated', handlePreferencesUpdate);
    
    return () => {
      window.removeEventListener('sidebar-preferences-updated', handlePreferencesUpdate);
    };
  }, []);

  return preferences;
};

// Mapping from sidebar item IDs to paths for preference filtering
// IMPORTANT: This must include ALL items from Settings.js sidebarItems array
const sidebarItemIdToPath = {
  // Essentials
  'dashboard': '/',
  'projects': '/projects',
  'calendar': '/calendar',
  'documents': '/documents',
  'team': '/team-workspace',
  'my-vault': '/workspace-files',
  'cms-dashboard': '/cms-dashboard',
  'contracts': '/contracts',
  'cms-receipt-acceptance': '/cws/receipt-acceptance',
  'cms-ratification': '/cws/ratification',
  'cms-fpds-reporting': '/cws/fpds-reporting',
  'cms-contract-analyzer': '/contract-analyzer',
  'cms-audit-trail': '/audit-dashboard',
  'cms-milestones': '/milestones',
  'cms-contract-catalog': '/cws/contract-catalog',
  'cms-sdvosb': '/sdvosb-tracker',
  'cms-risks': '/risks',
  'cms-portfolio-chat': '/cws/portfolio-chat',
  'cws-hub': '/cws',
  'acquisition-plan': '/acquisition-plan',
  'market-research': '/market-research',
  'igce-generator': '/igce-generator',
  'justification-approval': '/justification-approval',
  'synopsis-generator': '/synopsis-generator',
  'rfi-wizard': '/rfi-wizard',
  'clause-matrix': '/clause-matrix',
  'clause-recommender': '/clause-recommender',
  'evaluation-workbench': '/evaluation-workbench',
  'source-selection': '/source-selection',
  'evaluation-narrative': '/evaluation-narrative',
  'sf30-mod-engine': '/sf30-mod-engine',
  'va-library': '/va-library-new',
  
  // Tools
  'document-creator': '/document-creator',
  'vendors': '/vendor-database',
  'market-research': '/market-research',
  'igce-generator': '/igce-generator',
  'cws-hub': '/cws',
  'acquisition-plan': '/acquisition-plan',
  'synopsis-generator': '/synopsis-generator',
  'clause-matrix': '/clause-matrix',
  'justification-approval': '/justification-approval',
  'source-selection': '/source-selection',
  'compliance': '/compliance-checker',
  'gov-systems': '/gov-integrations',
  'meeting-notes': '/meeting-notes',
  'clause-recommender': '/clause-recommender',
  'decision-wizard': '/decision-wizard',
  'timeline': '/acquisition-timeline',
  'sdvosb-goals': '/sdvosb-tracker',
  'evaluation-tools': '/evaluation-tools',
  'contract-analyzer': '/contract-analyzer',
  'stakeholder-portals': '/stakeholder-portals',
  'quick-wins': '/quick-wins',
  'mobile-approvals': '/mobile-approval',
  'submissions': '/submissions',
  'evaluation-workbench': '/evaluation-workbench',
  'rfi-wizard': '/rfi-wizard',
  'vendor-portal': '/vendor-portal',
  
  // Insights
  'analytics': '/analytics',
  'leadership-view': '/leadership-analytics',
  'portfolio': '/portfolio',
  'pipeline': '/acquisition-pipeline',
  'audit-trail': '/audit-dashboard',
  'risk-monitor': '/risk-dashboard',
  'onboarding-analytics': '/onboarding-analytics',
  
  // System
  'settings': '/settings',
  'help': '/help',
  'administration': '/super-admin'
};

// Reverse mapping: path to item ID
const pathToSidebarItemId = Object.fromEntries(
  Object.entries(sidebarItemIdToPath).map(([id, path]) => [path, id])
);

// Items that are always visible regardless of preferences (locked items)
const lockedItemIds = [
  'acquisition-plan',
  'justification-approval',
  'source-selection',
  'synopsis-generator',
  'clause-matrix',
  'cms-dashboard',
  'contracts',
  'cms-receipt-acceptance',
  'cms-ratification',
  'cms-fpds-reporting',
  'cms-contract-analyzer',
  'cms-audit-trail',
  'cms-milestones',
  'cms-contract-catalog',
  'cms-sdvosb',
  'cms-risks',
  'cms-portfolio-chat',
  'acquisition-plan',
  'market-research',
  'igce-generator',
  'justification-approval',
  'synopsis-generator',
  'rfi-wizard',
  'clause-matrix',
  'clause-recommender',
  'evaluation-workbench',
  'source-selection',
  'cms-fitara',
  'evaluation-narrative',
  'sf30-mod-engine',
  'dashboard',
  'projects', 
  'calendar',
  'documents',
  'team',
  'va-library',  // Points to /va-library-new (the Tools version)
  'vendor-portal',  // Vendor Submission Portal Demo
  'settings',
  'help'
];

// Custom hook for mobile detection
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
};

// AI Status state - shared across components
const useAiStatus = () => {
  const [aiStatus, setAiStatus] = useState('healthy');
  const [aiLatency, setAiLatency] = useState(1.2);
  const [activeModel, setActiveModel] = useState('ACUMEN-10');
  const [isThinking, setIsThinking] = useState(false);

  useEffect(() => {
    let recoveryTimeout = null;
    
    const interval = setInterval(() => {
      // Randomly fluctuate latency - keep to 1 decimal place max
      setAiLatency(prev => {
        const fluctuation = (Math.random() - 0.5) * 0.3;
        const newLatency = Math.max(0.8, Math.min(1.5, prev + fluctuation));
        return Math.round(newLatency * 10) / 10;
      });
      
      // Simulate "thinking" occasionally (quick pulse)
      if (Math.random() < 0.15) {
        setIsThinking(true);
        setTimeout(() => setIsThinking(false), 800);
      }
      
      // Trigger failover very rarely (reduced by 75% - was 0.06, now 0.015)
      const shouldSimulateFailover = Math.random() < 0.015;
      
      if (shouldSimulateFailover && aiStatus === 'healthy' && activeModel === 'ACUMEN-10') {
        setActiveModel('ACUMEN-10B');
        setAiLatency(1.0);
        setAiStatus('failover');
        
        recoveryTimeout = setTimeout(() => {
          setActiveModel('ACUMEN-10');
          setAiLatency(1.1);
          setAiStatus('healthy');
        }, 1500 + Math.random() * 500);
      }
    }, 3000);

    return () => {
      clearInterval(interval);
      if (recoveryTimeout) clearTimeout(recoveryTimeout);
    };
  }, [aiStatus, activeModel]);

  // Sync to localStorage for other components
  useEffect(() => {
    const statusData = { model: activeModel, status: aiStatus, latency: aiLatency, timestamp: Date.now() };
    localStorage.setItem('acumen_status', JSON.stringify(statusData));
  }, [activeModel, aiStatus, aiLatency]);

  return { aiStatus, aiLatency, activeModel, isThinking };
};

// Logo Dropdown Component - Full size
const LogoDropdown = ({ darkMode, navigate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleResetDemo = () => {
    setIsOpen(false);
    // Clear everything immediately - don't wait for API
    clearAccessToken();
    localStorage.clear();
    sessionStorage.clear();
    // Fire and forget logout
    fetch(`${API_URL}/auth/logout`, { method: 'POST', credentials: 'include' }).catch(() => {});
    window.location.href = '/login';
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1 px-1 py-0.5 rounded transition-all ${
          darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
        }`}
      >
        <span className={`text-base font-bold ${darkMode ? 'text-white' : 'text-blue-600'}`}>
          Acu<span className="text-cyan-500">Sight</span>Pro
        </span>
        <span className={`text-[10px] font-medium px-1 py-0.5 rounded ${
          darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'
        }`}>Demo</span>
        <svg className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''} ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className={`absolute left-0 mt-2 w-48 rounded-xl shadow-2xl z-[100] overflow-hidden ${
          darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
        }`}>
          <div className="p-2">
            <button
              onClick={handleResetDemo}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                darkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-700'
              }`}
            >
              <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Reset Demo
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Logo Dropdown Component - Collapsed size
const CollapsedLogoDropdown = ({ darkMode, navigate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleResetDemo = () => {
    setIsOpen(false);
    // Clear everything immediately - don't wait for API
    clearAccessToken();
    localStorage.clear();
    sessionStorage.clear();
    // Fire and forget logout
    fetch(`${API_URL}/auth/logout`, { method: 'POST', credentials: 'include' }).catch(() => {});
    window.location.href = '/login';
  };

  return (
    <div className="relative mx-auto" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-center p-1 rounded transition-all ${
          darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
        }`}
        title="AcuSightPro Demo"
      >
        <span className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-blue-600'}`}>
          A<span className="text-cyan-500">S</span>
        </span>
        <svg className={`w-2.5 h-2.5 ml-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className={`absolute left-0 mt-2 w-40 rounded-xl shadow-2xl z-[100] overflow-hidden ${
          darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
        }`}>
          <div className="p-2">
            <button
              onClick={handleResetDemo}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                darkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-700'
              }`}
            >
              <svg className="w-3.5 h-3.5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Reset Demo
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Menu items defined outside component to avoid re-creation
// SIMPLIFIED NAVIGATION - Reduced from 25+ items to ~18 items
// Items have roleView to determine which role-specific menu they appear in
// roleView: 'co' (CO only), 'pm' (PM only), 'both' (appears in both)
const allMenuItems = [
  // ========== ESSENTIALS (Always visible - core daily workflow) ==========
  { path: '/', label: 'Dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6', keywords: ['home', 'overview', 'stats', 'budget', 'kpi', 'metrics'], coSection: 'essentials', pmSection: 'essentials', roleView: 'both' },
  { path: '/projects', label: 'Projects', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10', keywords: ['projects', 'cms-dashboard',
  'contracts', 'active', 'management', 'scope', 'schedule', 'budget'], coSection: 'essentials', pmSection: 'essentials', roleView: 'both' },
  { path: '/calendar', label: 'Calendar', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z', keywords: ['calendar', 'deadlines', 'dates', 'schedule', 'milestones', 'reminders', 'urgent'], coSection: 'essentials', pmSection: 'essentials', roleView: 'both' },
  { path: '/team-workspace', label: 'Team', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z', keywords: ['team', 'messages', 'workspace', 'notifications', 'coordination'], coSection: 'essentials', pmSection: 'essentials', unreadCount: 3, roleView: 'both' },
  { path: '/workspace-files', label: 'Workspace Files', icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z', keywords: ['vault', 'files', 'documents', 'storage', 'workspace'], coSection: 'essentials', pmSection: 'essentials', roleView: 'both' },

  { path: '/cms-dashboard', label: 'CMS Dashboard', icon: 'M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z', keywords: ['cms', 'dashboard', 'contracts', 'portfolio', 'alerts', 'options', 'invoices', 'cpars'], coSection: 'cms', pmSection: 'cms', roleView: 'both' },
  { path: '/contracts', label: 'Contracts', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', keywords: ['contracts', 'cms', 'modifications', 'options', 'deliverables', 'post-award', 'far 43', 'far 17', 'cdrl', 'cor'], coSection: 'cms', pmSection: 'cms', roleView: 'both' },
  { path: '/cws/receipt-acceptance', label: 'Receipt & Acceptance', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z', keywords: ['receipt', 'acceptance', 'cor', 'wawf', 'dd-250', 'cdrl', 'inspection'], coSection: 'cms', pmSection: 'cms', roleView: 'both' },
  { path: '/milestones', label: 'Milestones', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z', keywords: ['milestones', 'schedule', 'timeline', 'deadlines', 'due dates'], coSection: 'cms', pmSection: 'cms', roleView: 'both' },
  { path: '/audit-dashboard', label: 'Audit Trail', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01', keywords: ['audit', 'trail', 'log', 'history', 'gao', 'protest', 'compliance'], coSection: 'cms', pmSection: 'cms', roleView: 'both' },
  { path: '/cws/fpds-reporting', label: 'FPDS-NG Reporting', icon: 'M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z', keywords: ['fpds', 'reporting', 'award', 'data', 'usaspending'], coSection: 'cms', pmSection: 'cms', roleView: 'both' },
  { path: '/contract-analyzer', label: 'Contract Analyzer', icon: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z', keywords: ['contract', 'analyzer', 'ai', 'risk', 'clause', 'review', 'acumen'], coSection: 'cms', pmSection: 'cms', roleView: 'both' },
  { path: '/cws/portfolio-chat', label: 'Portfolio Chat', icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z', keywords: ['portfolio', 'chat', 'ai', 'acumen', 'ask', 'contracts'], coSection: 'cms', pmSection: 'cms', roleView: 'both' },
  { path: '/sdvosb-tracker', label: 'Veterans First Goals', icon: 'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z', keywords: ['sdvosb', 'veterans first', 'small business', 'goals', 'vosb'], coSection: 'cms', pmSection: 'cms', roleView: 'both' },
  { path: '/cws/contract-catalog', label: 'Contract Catalog', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10', keywords: ['contract', 'catalog', 'idiq', 'vehicles', 'gsa'], coSection: 'cms', pmSection: 'cms', roleView: 'both' },
  { path: '/risks', label: 'Risk Register', icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z', keywords: ['risk', 'register', 'issues', 'mitigation', 'monitor'], coSection: 'cms', pmSection: 'cms', roleView: 'both' },
  { path: '/cws/ratification', label: 'Ratification', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2', keywords: ['ratification', 'far 1.602-3', 'unauthorized', 'commitment'], coSection: 'cms', pmSection: 'cms', roleView: 'both' },


  { path: '/cws', label: 'ECWS Hub', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6', keywords: ['ecws', 'cws', 'hub', 'contract writing', 'pre-award', 'dashboard'], coSection: 'cws', pmSection: 'cws', roleView: 'both' },
  { path: '/acquisition-plan', label: 'Acquisition Plan', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2', keywords: ['acquisition plan', 'far 7.105', 'acqplan', 'planning'], coSection: 'cws', pmSection: 'cws', roleView: 'both' },
  { path: '/market-research', label: 'Market Research', icon: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z', keywords: ['market research', 'sources sought', 'rfi', 'far 10', 'mrr'], coSection: 'cws', pmSection: 'cws', roleView: 'both' },
  { path: '/igce-generator', label: 'IGCE Generator', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z', keywords: ['igce', 'independent government cost estimate', 'price', 'far 36.203'], coSection: 'cws', pmSection: 'cws', roleView: 'both' },
  { path: '/justification-approval', label: 'Justification & Approval', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z', keywords: ['j&a', 'justification', 'approval', 'far 6.303', 'sole source'], coSection: 'cws', pmSection: 'cws', roleView: 'both' },
  { path: '/synopsis-generator', label: 'Synopsis Generator', icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9', keywords: ['synopsis', 'sam.gov', 'pre-solicitation', 'far 5.2', 'notice'], coSection: 'cws', pmSection: 'cws', roleView: 'both' },
  { path: '/rfi-wizard', label: 'RFI Wizard', icon: 'M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z', keywords: ['rfi', 'request for information', 'sources sought', 'market research'], coSection: 'cws', pmSection: 'cws', roleView: 'both' },
  { path: '/clause-matrix', label: 'Clause Matrix', icon: 'M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z', keywords: ['clause', 'matrix', 'far', 'dfars', 'vaar', 'clauses'], coSection: 'cws', pmSection: 'cws', roleView: 'both' },
  { path: '/clause-recommender', label: 'Clause Recommender', icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z', keywords: ['clause', 'recommender', 'ai', 'far', 'acumen'], coSection: 'cws', pmSection: 'cws', roleView: 'both' },
  { path: '/evaluation-workbench', label: 'Evaluation Workbench', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01', keywords: ['evaluation', 'sseb', 'source selection', 'far 15.305', 'ratings'], coSection: 'cws', pmSection: 'cws', roleView: 'both' },
  { path: '/source-selection', label: 'Source Selection', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z', keywords: ['source selection', 'acumen-10', 'tradeoff', 'best value', 'far 15.308'], coSection: 'cws', pmSection: 'cws', roleView: 'both' },
  { path: '/cws/fitara-review', label: 'FITARA CIO Review', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z', keywords: ['fitara', 'cio', 'it review', 'far 39', 'technology'], coSection: 'cws', pmSection: 'cws', roleView: 'both' },
  { path: '/evaluation-narrative', label: 'Evaluation Narrative', icon: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z', keywords: ['evaluation', 'narrative', 'sseb', 'far 15.305', 'rating', 'offeror'], coSection: 'cws', pmSection: 'cws', roleView: 'both' },
  { path: '/sf30-mod-engine', label: 'SF-30 Mod Engine', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', keywords: ['sf-30', 'modification', 'mod', 'amendment', 'far 43', 'bilateral', 'unilateral'], coSection: 'cws', pmSection: 'cws', roleView: 'both' },
  // ========== TOOLS (Primary work tools) ==========
  { path: '/va-library-new', label: 'VA Library', icon: 'M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z', keywords: ['va', 'library', 'veterans', 'affairs'], coSection: 'tools', pmSection: 'tools', roleView: 'both' },
  { path: '/document-creator', label: 'Document Creator', icon: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z', keywords: ['document', 'create', 'write', 'sow', 'pws', 'requirements', 'editor'], coSection: 'tools', pmSection: 'tools', roleView: 'both' },
  { path: '/vendor-database', label: 'Vendors', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4', keywords: ['vendor', 'sam', 'sdvosb', 'vosb', 'contractor', 'cpars', 'performance', 'database'], coSection: 'tools', pmSection: 'tools', roleView: 'both' },
  { path: '/market-research', label: 'Market Research', icon: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7', keywords: ['market', 'research', 'sources', 'sought'], coSection: 'tools', pmSection: 'tools', roleView: 'both' },
  { path: '/igce-generator', label: 'IGCE Generator', icon: 'M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z', keywords: ['igce', 'cost', 'estimate', 'budget', 'labor', 'rates'], coSection: 'tools', pmSection: 'tools', roleView: 'both' },
  { path: '/synopsis-generator', label: 'Synopsis Generator', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', keywords: ['synopsis', 'pre-solicitation', 'sam.gov', 'sources sought', 'notice', 'far 5.201'], coSection: 'tools', pmSection: 'tools', roleView: 'both' },
  { path: '/clause-matrix', label: 'Clause Matrix', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4', keywords: ['clause', 'matrix', 'far 52', 'vaar 852', 'mandatory', 'clauses', 'solicitation'], coSection: 'tools', pmSection: 'tools', roleView: 'both' },
    { path: '/acquisition-plan', label: 'Acquisition Plan', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01', keywords: ['acquisition', 'plan', 'ap', 'far', '7.105', 'planning', 'wizard', 'milestones', 'palt'], coSection: 'tools', pmSection: 'tools', roleView: 'both' },
  { path: '/justification-approval', label: 'Justification & Approval', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z', keywords: ['justification', 'approval', 'j&a', 'sole source', 'far 6.302', 'brand name', 'urgency', 'non-competitive'], coSection: 'tools', pmSection: 'tools', roleView: 'both' },
  { path: '/source-selection', label: 'Source Selection', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z', keywords: ['source', 'selection', 'evaluation', 'acumen', 'vendors', 'scoring', 'dimensions', 'analysis'], coSection: 'tools', pmSection: 'tools', roleView: 'both' },
  { path: '/compliance-checker', label: 'Compliance', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z', keywords: ['compliance', 'far', 'vaar', 'audit', 'check', 'sdvosb', 'small business'], coSection: 'tools', pmSection: 'tools', roleView: 'both' },
  { path: '/gov-integrations', label: 'Gov Systems', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4', keywords: ['fpds', 'usaspending', 'cpars', 'gsa', 'ebuy', 'sam', 'teams', 'outlook', 'calendar', 'integrations'], coSection: 'tools', pmSection: 'tools', roleView: 'both' },
  { path: '/rfi-wizard', label: 'Test RFI', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', keywords: ['rfi', 'wizard', 'test', 'request', 'information', 'create'], coSection: 'tools', pmSection: 'tools', roleView: 'both' },
  { path: '/post-portal', label: 'Post Portal', icon: 'M12 19l9 2-9-18-9 18 9-2zm0 0v-8', keywords: ['post', 'portal', 'sam', 'publish', 'solicitation', 'rfi', 'rfp', 'rfq'], coSection: 'tools', pmSection: 'tools', roleView: 'both' },
  { path: '/vendor-portal', label: 'Vendor Portal', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z', keywords: ['vendor', 'response', 'submit', 'rfp', 'rfi', 'portal', 'submission', 'demo'], coSection: 'tools', pmSection: 'tools', roleView: 'both' },

  // ========== INSIGHTS (Analytics & tracking) ==========
  { path: '/analytics', label: 'Analytics', icon: 'M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z', keywords: ['analytics', 'metrics', 'reports', 'charts', 'kpi', 'evm', 'performance'], coSection: 'insights', pmSection: 'insights', roleView: 'both' },
  { path: '/leadership-analytics', label: 'Leadership View', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z', keywords: ['leadership', 'analytics', 'kpi', 'metrics', 'cost avoidance', 'lead time', 'budget', 'workload', 'small business'], coSection: 'insights', pmSection: 'insights', roleView: 'both' },
  { path: '/portfolio', label: 'Portfolio', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z', keywords: ['radar', 'pipeline', 'opportunities', 'tracking', 'program', 'portfolio'], coSection: 'insights', pmSection: 'insights', roleView: 'both' },
  { path: '/acquisition-pipeline', label: 'Pipeline', icon: 'M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z', keywords: ['pipeline', 'kanban', 'lifecycle', 'phases', 'workflow', 'acquisition'], coSection: 'insights', pmSection: 'insights', roleView: 'both' },
  { path: '/audit-dashboard', label: 'Audit Trail', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01', keywords: ['audit', 'compliance', 'trail', 'protest', 'defense', 'far', 'dfars'], coSection: 'insights', pmSection: 'insights', roleView: 'both' },
  { path: '/risk-dashboard', label: 'Risk Monitor', icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z', keywords: ['risk', 'analysis', 'dashboard', 'register', 'issues', 'mitigation'], coSection: 'insights', pmSection: 'insights', roleView: 'both' },
  { path: '/proposal-evaluator', label: 'Proposal Evaluator', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4', keywords: ['proposal', 'evaluation', 'source', 'selection', 'vendor', 'scoring', 'ai'], coSection: 'insights', pmSection: 'insights', roleView: 'both' },
  { path: '/onboarding-analytics', label: 'Onboarding Analytics', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z', keywords: ['onboarding', 'analytics', 'funnel', 'completion', 'wizard', 'metrics'], coSection: 'insights', pmSection: 'insights', roleAccess: 'admin', roleView: 'both' },

  // ========== SYSTEM (Settings & admin) ==========
  { path: '/settings', label: 'Settings', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z', keywords: ['settings', 'preferences', 'dark mode'], coSection: 'system', pmSection: 'system', roleView: 'both' },
  { path: '/help', label: 'Help', icon: 'M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z', keywords: ['help', 'support', 'faq'], coSection: 'tools', pmSection: 'tools', roleView: 'both' },
  { path: '/super-admin', label: 'Administration', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z', keywords: ['admin', 'super', 'management', 'users'], coSection: 'superAdmin', pmSection: 'superAdmin', roleAccess: 'super_admin', roleView: 'both' },

  // ========== HIDDEN BUT SEARCHABLE (accessible via search, not in main menu) ==========
  // These items are still accessible via the search bar and direct URLs
  { path: '/evaluation-tools', label: 'Evaluation Tools', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4', keywords: ['evaluation', 'proposal', 'analyze', 'compare', 'rates', 'workbench', 'scoring'], coSection: 'tools', pmSection: 'tools', roleView: 'both' },
  { path: '/clause-recommender', label: 'Clause Recommender', icon: 'M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3', keywords: ['clause', 'far', 'dfars', 'recommend', 'ai', 'compliance'], coSection: 'tools', pmSection: 'tools', roleView: 'both' },
  { path: '/contract-analyzer', label: 'Contract Analyzer', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', keywords: ['contract', 'analyze', 'risk', 'clause', 'ai'], coSection: 'tools', pmSection: 'tools', roleView: 'both' },

  { path: '/cws/gao-twin', label: 'GAO Twin', icon: 'M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3', keywords: ['gao', 'protest', 'twin', 'simulator', 'bid protest', 'pre-award', 'risk'], coSection: 'cws', pmSection: 'cws', roleView: 'both' },
  { path: '/cws/solicitation-builder', label: 'Solicitation Builder', icon: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z', keywords: ['solicitation', 'builder', 'ucf', 'sow', 'pws', 'far', 'dfars', 'vaar', 'clause', 'clin', 'best value', 'tradeoff', 'wizard'], coSection: 'cws', pmSection: 'cws', roleView: 'both' },
  { path: '/cws/portfolio-chat', label: 'Portfolio Chat', icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z', keywords: ['portfolio', 'chat', 'cms-dashboard',
  'contracts', 'ask', 'questions', 'acumen', 'ai'], coSection: 'cws', pmSection: 'cws', roleView: 'both' },

  { path: '/cws/real-property-leasing', label: 'Real Property Leasing', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4', keywords: ['real property', 'lease', 'leasing', 'sfo', 'vaar 870', 'gsa l100', 'pbs', 'vha', 'vba', 'facility'], coSection: 'cws', pmSection: 'cws', roleView: 'both' },
  { path: '/cws/ordering-officer', label: 'Ordering Officer Mgmt', icon: 'M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z', keywords: ['ordering officer', 'idiq', 'bpa', 'task order', 'delegation', 'far 16.5', 'far 1.602', 'authority', 'ceiling'], coSection: 'cms', pmSection: 'cms', roleView: 'both' },
  { path: '/stakeholder-portals', label: 'Stakeholder Portals', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z', keywords: ['stakeholder', 'collaboration', 'legal', 'finance', 'vendor', 'requirements', 'portal', 'sla'], coSection: 'tools', pmSection: 'tools', roleView: 'both' },
  { path: '/quick-wins', label: 'Quick Wins', icon: 'M13 10V3L4 14h7v7l9-11h-7z', keywords: ['quick', 'milestone', 'calculator', 'document', 'package', 'clone', 'alerts', 'deadline', 'productivity'], coSection: 'tools', pmSection: 'tools', roleView: 'both' },
  { path: '/decision-wizard', label: 'Decision Wizard', icon: 'M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z', keywords: ['decision', 'wizard', 'procurement', 'method', 'threshold', 'guide', 'help'], coSection: 'tools', pmSection: 'tools', roleView: 'both' },
  { path: '/mobile-approval', label: 'Mobile Approvals', icon: 'M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z', keywords: ['mobile', 'approval', 'phone', 'review', 'approve', 'reject', 'notification', 'push'], coSection: 'tools', pmSection: 'tools', roleView: 'both' },
  { path: '/sdvosb-tracker', label: 'SDVOSB Goals', icon: 'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z', keywords: ['sdvosb', 'vosb', 'small business', 'veteran', 'goals'], coSection: 'tools', pmSection: 'tools', roleView: 'both' },
  { path: '/submissions', label: 'Vendor Submissions', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', keywords: ['submission', 'vendor', 'proposal', 'response'], coSection: 'tools', pmSection: 'tools', roleView: 'both' },
  { path: '/evaluation-workbench', label: 'Evaluation Workbench', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z', keywords: ['evaluation', 'scoring', 'matrix', 'ranking'], coSection: 'tools', pmSection: 'tools', roleView: 'both' },
  { path: '/acquisition-timeline', label: 'Timeline', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z', keywords: ['timeline', 'cradle', 'grave', 'lifecycle', 'phases', 'schedule', 'milestones'], coSection: 'tools', pmSection: 'tools', roleView: 'both' },
];

// Section definitions - SIMPLIFIED from 5+ sections to 4 sections
const coSections = [
  { id: 'essentials', label: '', color: 'blue', alwaysOpen: true, hidden: true },
  { id: 'cms', label: 'CMS', color: 'green' },
  { id: 'cws', label: 'ECWS', color: 'amber', highlight: true },
  { id: 'tools', label: 'Tools', color: 'cyan' },
  { id: 'insights', label: 'Insights', color: 'purple' },
  { id: 'system', label: 'System', color: 'gray' },
  { id: 'superAdmin', label: 'Administration', color: 'red', hidden: true },
  { id: 'hidden', label: '', color: 'gray', hidden: true, alwaysOpen: false }, // Hidden items for search only
];

// Section definitions for PM view (same simplified structure)
const pmSections = [
  { id: 'essentials', label: '', color: 'blue', alwaysOpen: true, hidden: true },
  { id: 'cms', label: 'CMS', color: 'green' },
  { id: 'cws', label: 'ECWS', color: 'amber', highlight: true },
  { id: 'tools', label: 'Tools', color: 'cyan' },
  { id: 'insights', label: 'Insights', color: 'purple' },
  { id: 'system', label: 'System', color: 'gray' },
  { id: 'superAdmin', label: 'Administration', color: 'red', hidden: true },
  { id: 'hidden', label: '', color: 'gray', hidden: true, alwaysOpen: false }, // Hidden items for search only
];

function Sidebar({ mobileMenuOpen, setMobileMenuOpen }) {
  const [collapsed, setCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  // Initialize collapsed state from localStorage with defaults (tools, insights, system collapsed by default)
  const [manuallyCollapsed, setManuallyCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebar_collapsed_sections');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      logger.error('Error loading sidebar state:', e);
    }
    // Default: collapse non-essential sections to reduce clutter
    return {
      cws: false, // ECWS section expanded by default
      tools: true,
      insights: true,
      system: true,
      superAdmin: true
    };
  });
  
  // Clear stale ECWS collapse state from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('sidebar_collapsed_sections');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.cws === true) {
          parsed.cws = false;
          localStorage.setItem('sidebar_collapsed_sections', JSON.stringify(parsed));
          setManuallyCollapsed(prev => ({ ...prev, cws: false }));
        }
      }
    } catch(e) {}
  }, []);

  // Fetch sidebar preferences from API
  const sidebarPreferences = useSidebarPreferences();
  
  const location = useLocation();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const { aiStatus, aiLatency, activeModel, isThinking } = useAiStatus();
  const { jobRole, hasAccess, viewAsRole, getEffectiveRole } = useRole();
  const isMobile = useIsMobile();
  
  // Persist collapsed state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('sidebar_collapsed_sections', JSON.stringify(manuallyCollapsed));
    } catch (e) {
      logger.error('Error saving sidebar state:', e);
    }
  }, [manuallyCollapsed]);
  
  // Auto-close mobile menu on navigation
  useEffect(() => {
    if (isMobile && mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Handle navigation click - close mobile menu
  const handleNavClick = useCallback(() => {
    if (isMobile && mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  }, [isMobile, mobileMenuOpen, setMobileMenuOpen]);

  // Filter menu items based on user's effective role (considering viewAs for super admin)
  const effectiveRole = getEffectiveRole();
  const isCoView = effectiveRole === 'co' || effectiveRole === 'super_admin' || effectiveRole === 'hybrid';
  const isPmView = effectiveRole === 'pm';
  
  // Get sections based on role
  const sections = isPmView ? pmSections : coSections;
  
  // Helper function to check if an item should be visible based on preferences
  const isItemVisibleByPreference = useCallback((itemPath) => {
    // If preferences haven't loaded yet, show all items
    if (!sidebarPreferences.loaded) return true;
    
    // If no preferences set (empty array means show defaults), show all items
    if (sidebarPreferences.visible_items.length === 0) return true;
    
    const itemId = pathToSidebarItemId[itemPath];
    if (!itemId) return true; // Unknown items are always shown
    
    // Locked items are always visible
    if (lockedItemIds.includes(itemId)) return true;
    
    // Check if item is in the visible list
    return sidebarPreferences.visible_items.includes(itemId);
  }, [sidebarPreferences]);

  // Helper function to get sort order for an item
  const getItemSortOrder = useCallback((itemPath) => {
    const itemId = pathToSidebarItemId[itemPath];
    if (!itemId) return 999; // Unknown items at end
    
    const orderList = sidebarPreferences.item_order || [];
    const index = orderList.indexOf(itemId);
    return index === -1 ? 999 : index; // Items not in order list go to end
  }, [sidebarPreferences]);
  
  // Get menu items for the current role view, filtered by preferences and sorted by order
  const filteredMenuItems = useMemo(() => {
    const sectionKey = isPmView ? 'pmSection' : 'coSection';
    
    const filtered = allMenuItems.filter(item => {
      // Check role view (co, pm, or both)
      if (item.roleView === 'co' && isPmView) return false;
      if (item.roleView === 'pm' && !isPmView) return false;
      
      // Check if item has a section for this view
      if (!item[sectionKey]) return false;
      
      // Check roleAccess for special items like super_admin
      if (item.roleAccess === 'super_admin' && effectiveRole !== 'super_admin') return false;
      
      // Check sidebar preferences (skip for hidden items - they're only for search)
      if (item[sectionKey] !== 'hidden' && !isItemVisibleByPreference(item.path)) return false;
      
      return true;
    }).map(item => ({
      ...item,
      section: item[sectionKey], // Use the correct section for this role
      sortOrder: getItemSortOrder(item.path)
    }));

    // Sort items by their custom order within each section
    return filtered.sort((a, b) => {
      // First sort by section (keep section grouping)
      if (a.section !== b.section) return 0;
      // Then by custom order
      return a.sortOrder - b.sortOrder;
    });
  }, [isPmView, effectiveRole, isItemVisibleByPreference, getItemSortOrder]);

  // Filter sections that have items
  const visibleSections = useMemo(() => {
    return sections.filter(section => {
      const items = filteredMenuItems.filter(item => item.section === section.id);
      return items.length > 0;
    });
  }, [filteredMenuItems, sections]);

  // Compute expanded sections: Daily is always open, others follow accordion behavior
  const currentItem = filteredMenuItems.find(item => item.path === location.pathname);
  const currentSection = currentItem?.section;
  
  // Initialize expanded state - Daily always open, current section open, others closed
  const expandedSections = useMemo(() => {
    const expanded = {};
    visibleSections.forEach(section => {
      if (section.alwaysOpen) {
        // Daily Essentials always open
        expanded[section.id] = true;
      } else if (manuallyCollapsed[section.id] !== undefined) {
        // Respect manual toggle
        expanded[section.id] = !manuallyCollapsed[section.id];
      } else {
        // Auto-expand if contains current route
        expanded[section.id] = currentSection === section.id;
      }
    });
    return expanded;
  }, [visibleSections, currentSection, manuallyCollapsed]);

  const toggleSection = (sectionId) => {
    // Find section config
    const sectionConfig = sections.find(s => s.id === sectionId);
    
    // Don't allow collapsing "always open" sections
    if (sectionConfig?.alwaysOpen) return;
    
    const isCurrentlyExpanded = expandedSections[sectionId];
    
    if (isCurrentlyExpanded) {
      // Collapse this section
      setManuallyCollapsed(prev => ({ 
        ...prev, 
        [sectionId]: true
      }));
    } else {
      // Expand this section, collapse others (except alwaysOpen)
      const newState = {};
      visibleSections.forEach(s => {
        if (s.alwaysOpen) {
          newState[s.id] = false; // Keep always-open sections open (false = not collapsed)
        } else {
          newState[s.id] = s.id !== sectionId; // Collapse all except clicked
        }
      });
      setManuallyCollapsed(newState);
    }
  };

  const getItemsBySection = (sectionId) => filteredMenuItems.filter(item => item.section === sectionId);

  const getSearchResults = () => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();
    return filteredMenuItems.filter(item => 
      item.label.toLowerCase().includes(query) ||
      item.keywords?.some(k => k.includes(query))
    );
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const results = getSearchResults();
    if (results.length > 0) {
      navigate(results[0].path);
      setSearchQuery('');
      setShowSearchResults(false);
    }
  };

  const handleSearchSelect = (path) => {
    navigate(path);
    setSearchQuery('');
    setShowSearchResults(false);
  };

  const searchResults = getSearchResults();

  const getSectionColors = (sectionId) => {
    const colors = {
      // New simplified sections
      essentials: { active: darkMode ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-50 text-blue-700', icon: darkMode ? 'text-blue-400' : 'text-blue-600' },
      tools: { active: darkMode ? 'bg-cyan-900/50 text-cyan-400' : 'bg-cyan-50 text-cyan-700', icon: darkMode ? 'text-cyan-400' : 'text-cyan-600' },
      insights: { active: darkMode ? 'bg-purple-900/50 text-purple-400' : 'bg-purple-50 text-purple-700', icon: darkMode ? 'text-purple-400' : 'text-purple-600' },
      system: { active: darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900', icon: '' },
      superAdmin: { active: darkMode ? 'bg-red-900/50 text-red-400' : 'bg-red-50 text-red-700', icon: darkMode ? 'text-red-400' : 'text-red-600' },
      hidden: { active: darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900', icon: '' },
      // Legacy section names for backward compatibility
      daily: { active: darkMode ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-50 text-blue-700', icon: darkMode ? 'text-blue-400' : 'text-blue-600' },
      working: { active: darkMode ? 'bg-cyan-900/50 text-cyan-400' : 'bg-cyan-50 text-cyan-700', icon: darkMode ? 'text-cyan-400' : 'text-cyan-600' },
      periodic: { active: darkMode ? 'bg-green-900/50 text-green-400' : 'bg-green-50 text-green-700', icon: darkMode ? 'text-green-400' : 'text-green-600' },
      tracking: { active: darkMode ? 'bg-purple-900/50 text-purple-400' : 'bg-purple-50 text-purple-700', icon: darkMode ? 'text-purple-400' : 'text-purple-600' },
      admin: { active: darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900', icon: '' },
    };
    return colors[sectionId] || colors.essentials;
  };

  const renderSectionHeader = (section) => {
    // Don't render header for hidden sections (daily essentials)
    if (section.hidden) return null;
    
    const isExpanded = expandedSections[section.id];
    const isAlwaysOpen = section.alwaysOpen;
    
    return (
      <button
        key={`header-${section.id}`}
        id={`header-${section.id}`}
        data-testid={`sidebar-section-${section.id}`}
        onClick={(e) => {
          e.stopPropagation();
          toggleSection(section.id);
        }}
        disabled={isAlwaysOpen}
        className={`relative z-10 w-full px-4 py-2.5 flex items-center justify-between text-xs font-semibold uppercase tracking-wider transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-inset ${darkMode ? 'bg-gray-900' : 'bg-white'} ${
          isAlwaysOpen 
            ? (darkMode ? 'text-blue-400' : 'text-blue-600')
            : (darkMode ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-800/50' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50')
        } ${isAlwaysOpen ? 'cursor-default' : 'cursor-pointer'}`}
        aria-expanded={isExpanded}
        aria-controls={`section-${section.id}`}
        aria-label={`${section.label} menu, ${isExpanded ? 'expanded' : 'collapsed'}`}
      >
        <span>{section.label}</span>
        {!isAlwaysOpen && (
          <svg
            className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-0' : '-rotate-90'}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </button>
    );
  };

  const renderMenuItems = (sectionId) => {
    const items = getItemsBySection(sectionId);
    const colors = getSectionColors(sectionId);
    const isExpanded = expandedSections[sectionId];

    return (
      <div 
        id={`section-${sectionId}`}
        role="region"
        aria-labelledby={`header-${sectionId}`}
        className={`relative z-20 transition-all duration-200 ease-in-out ${
          isExpanded ? 'opacity-100' : 'max-h-0 opacity-0 overflow-hidden pointer-events-none'
        } ${isExpanded && (sectionId === 'tools' || sectionId === 'cms' || sectionId === 'cws') ? 'max-h-[320px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent' : isExpanded ? 'max-h-[500px]' : ''}`}
      >
        <nav className="px-2 pt-2 pb-1 space-y-1" aria-label={`${sectionId} navigation`}>
          {items.map((item, index) => {
            const isActive = location.pathname === item.path;
            const isLastItem = index === items.length - 1;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-link-${item.path.replace(/\//g, '-').replace(/^-/, '')}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleNavClick();
                }}
                aria-label={`${item.label}${item.isNew ? ' (New feature)' : ''}${item.unreadCount > 0 ? `, ${item.unreadCount} unread` : ''}`}
                aria-current={isActive ? 'page' : undefined}
                className={`relative z-30 block flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500 cursor-pointer ${
                  isLastItem ? 'mb-3' : ''
                } ${
                  isActive
                    ? colors.active
                    : darkMode 
                      ? 'text-gray-300 hover:bg-gray-700 hover:text-white' 
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
                title={collapsed ? item.label : ''}
                >
                  {item.useAIIcon ? (
                    <div className={`${collapsed ? '' : 'mr-3'}`}>
                      <AcumenAICircle size="xs" />
                    </div>
                  ) : (
                    <svg className={`w-5 h-5 ${collapsed ? '' : 'mr-3'} ${isActive ? colors.icon : ''} pointer-events-none`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                    </svg>
                  )}
                  {!collapsed && (
                    <span className="flex items-center gap-2 flex-1 pointer-events-none">
                      {item.label}
                      {item.isNew && (
                        <span className="px-1.5 py-0.5 text-[10px] font-bold bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-full pointer-events-none" aria-hidden="true">NEW</span>
                      )}
                      {item.unreadCount > 0 && !isActive && (
                        <span className="ml-auto px-1.5 py-0.5 text-[10px] font-bold bg-red-500 text-white rounded-full min-w-[18px] text-center pointer-events-none" aria-hidden="true">
                          {item.unreadCount}
                        </span>
                      )}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
        </div>
    );
  };

  // Mobile overlay and sidebar visibility logic
  const sidebarClasses = isMobile
    ? `fixed inset-y-0 left-0 z-50 flex flex-col transition-transform duration-300 w-64 ${
        mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      } ${darkMode ? 'bg-gray-800 border-r border-gray-700' : 'bg-white border-r border-gray-200'}`
    : `flex flex-col transition-all duration-300 relative z-10 ${collapsed ? 'w-16' : 'w-64'} ${
        darkMode ? 'bg-gray-800 border-r border-gray-700' : 'bg-white border-r border-gray-200'
      }`;

  return (
    <>
      {/* Mobile overlay backdrop */}
      {isMobile && mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}
      
      <aside 
        className={sidebarClasses}
        role="navigation"
        aria-label="Main navigation"
      >
      {/* Header with Logo + ACUMEN */}
      <div className={`p-4 relative z-20 ${darkMode ? 'border-b border-gray-700' : 'border-b border-gray-100'}`}>
        {/* Logo Row */}
        <div className="flex items-center justify-between mb-3">
          {!collapsed ? (
            <LogoDropdown darkMode={darkMode} navigate={navigate} />
          ) : (
            <CollapsedLogoDropdown darkMode={darkMode} navigate={navigate} />
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={`p-1 rounded ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
          >
            <svg className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={collapsed ? 'M9 5l7 7-7 7' : 'M15 19l-7-7 7-7'} />
            </svg>
          </button>
        </div>

        {/* ACUMEN AI Status Indicator */}
        {!collapsed ? (
          <div 
            onClick={() => navigate('/ai-learning?tab=contingency')}
            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer transition-all ${
              darkMode 
                ? 'bg-gray-750 hover:bg-gray-700 border border-gray-700' 
                : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            {/* Futuristic AI Status Icon */}
            <div className="relative w-8 h-8">
              <div className={`absolute inset-0 rounded-lg ${
                aiStatus === 'failover'
                  ? (darkMode ? 'bg-blue-900/50' : 'bg-blue-100')
                  : (darkMode ? 'bg-green-900/50' : 'bg-green-100')
              }`} />
              
              {/* Outer spinning ring */}
              <div className="absolute inset-0 acumen-spin">
                <svg className="w-full h-full" viewBox="0 0 32 32">
                  <circle 
                    cx="16" cy="16" r="12" 
                    fill="none" 
                    stroke={aiStatus === 'failover' ? 'rgba(59,130,246,0.2)' : 'rgba(34,197,94,0.2)'}
                    strokeWidth="2"
                  />
                  <circle 
                    cx="16" cy="16" r="12" 
                    fill="none" 
                    stroke={aiStatus === 'failover' ? '#3b82f6' : '#22c55e'}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeDasharray="18.8 56.5"
                  />
                </svg>
              </div>
              
              {/* Inner counter-spinning ring */}
              <div className="absolute inset-1 acumen-spin-reverse">
                <svg className="w-full h-full" viewBox="0 0 32 32">
                  <circle 
                    cx="16" cy="16" r="10" 
                    fill="none" 
                    stroke={aiStatus === 'failover' ? 'rgba(59,130,246,0.3)' : 'rgba(34,197,94,0.3)'}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeDasharray="12 50"
                  />
                </svg>
              </div>
              
              {/* Center dot */}
              <div className={`absolute inset-0 flex items-center justify-center`}>
                <div className={`w-2 h-2 rounded-full ${
                  aiStatus === 'failover' ? 'bg-blue-500' : 'bg-green-500'
                } ${isThinking ? 'animate-pulse' : ''}`} />
              </div>
            </div>
            
            {/* Model name and latency */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className={`text-sm font-bold ${
                  activeModel === 'ACUMEN-10B' 
                    ? (darkMode ? 'text-blue-400' : 'text-blue-600')
                    : (darkMode ? 'text-white' : 'text-gray-900')
                }`}>
                  {activeModel}
                </span>
                <div className={`w-1.5 h-1.5 rounded-full ${
                  aiStatus === 'failover' ? 'bg-blue-500' : 'bg-green-500'
                } ${isThinking ? 'animate-pulse' : ''}`} />
              </div>
              <div className="flex items-center gap-1">
                <span className={`text-xs font-mono ${
                  aiStatus === 'failover' ? 'text-blue-500' : 'text-green-500'
                }`}>
                  {aiLatency}s
                </span>
                <span className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  latency
                </span>
              </div>
            </div>
          </div>
        ) : (
          /* Collapsed ACUMEN indicator */
          <div 
            onClick={() => navigate('/ai-learning?tab=contingency')}
            className={`flex items-center justify-center p-2 rounded-lg cursor-pointer transition-all ${
              darkMode ? 'bg-gray-750 hover:bg-gray-700' : 'bg-gray-50 hover:bg-gray-100'
            }`}
            title={`${activeModel} • ${aiLatency}s`}
          >
            <div className="relative w-6 h-6">
              {/* Spinning ring for collapsed state */}
              <div className="absolute inset-0 acumen-spin">
                <svg className="w-full h-full" viewBox="0 0 24 24">
                  <circle 
                    cx="12" cy="12" r="9" 
                    fill="none" 
                    stroke={aiStatus === 'failover' ? 'rgba(59,130,246,0.3)' : 'rgba(34,197,94,0.3)'}
                    strokeWidth="2"
                  />
                  <circle 
                    cx="12" cy="12" r="9" 
                    fill="none" 
                    stroke={aiStatus === 'failover' ? '#3b82f6' : '#22c55e'}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeDasharray="14 42"
                  />
                </svg>
              </div>
              <div className={`absolute inset-0 flex items-center justify-center`}>
                <div className={`w-1.5 h-1.5 rounded-full ${
                  aiStatus === 'failover' ? 'bg-blue-500' : 'bg-green-500'
                }`} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Role Switcher removed - auto-assign CO role */}

      {/* Search */}
      {!collapsed && (
        <div className="p-4">
          <form onSubmit={handleSearch} className="relative">
            <div className="flex gap-1">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowSearchResults(e.target.value.length > 0);
                  }}
                  onFocus={() => searchQuery && setShowSearchResults(true)}
                  onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
                  placeholder="Search features..."
                  className={`w-full pl-9 pr-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                      : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                  } border`}
                />
                <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <button
                type="submit"
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  darkMode
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                Go
              </button>
            </div>
            
            {/* Search Results Dropdown */}
            {showSearchResults && searchResults.length > 0 && (
              <div className={`absolute top-full left-0 right-0 mt-1 rounded-lg shadow-lg border z-50 ${
                darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              }`}>
                {searchResults.map((item) => (
                  <button
                    key={item.path}
                    type="button"
                    onClick={() => handleSearchSelect(item.path)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors ${
                      darkMode 
                        ? 'text-gray-200 hover:bg-gray-700' 
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                    </svg>
                    {item.label}
                  </button>
                ))}
              </div>
            )}
            
            {showSearchResults && searchQuery && searchResults.length === 0 && (
              <div className={`absolute top-full left-0 right-0 mt-1 rounded-lg shadow-lg border z-50 px-3 py-2 text-sm ${
                darkMode ? 'bg-gray-800 border-gray-700 text-gray-400' : 'bg-white border-gray-200 text-gray-500'
              }`}>
                No results found
              </div>
            )}
          </form>
        </div>
      )}

      {/* Back Button removed - now in TopBar for consistent placement */}

      {/* Menu Sections */}
      <div className="flex-1 overflow-y-auto pt-1">
        {/* Favorites section removed - customization now available in Settings > Sidebar Preferences */}
        
        {/* ========== REGULAR MENU SECTIONS ========== */}
        {visibleSections.map((section) => (
          <div key={section.id} className="mb-1 relative">
            {!collapsed && renderSectionHeader(section)}
            {collapsed && !isMobile ? (
              <nav className="px-2 space-y-1">
                {getItemsBySection(section.id).map((item) => {
                  const isActive = location.pathname === item.path;
                  const colors = getSectionColors(section.id);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={handleNavClick}
                      className={`flex items-center justify-center p-2.5 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? colors.active
                          : darkMode 
                            ? 'text-gray-300 hover:bg-gray-700 hover:text-white' 
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                      title={item.label}
                    >
                      <svg className={`w-5 h-5 ${isActive ? colors.icon : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                      </svg>
                    </Link>
                  );
                })}
              </nav>
            ) : (
              renderMenuItems(section.id)
            )}
          </div>
        ))}
      </div>
      
      {/* Offline Status Indicator */}
      {!collapsed && (
        <div className={`p-3 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <OfflineStatusBadge darkMode={darkMode} compact={true} />
        </div>
      )}
    </aside>
    </>
  );
}

export default Sidebar;
